import { useId } from "react";
import { LuCircleCheck } from "react-icons/lu";
import { Controller, useFormContext } from "react-hook-form";

import { Field, FieldError } from "./fields";
import OtpInput from "./OtpInput";
import PhoneInput from "./PhoneInput";
import { DEMO_CODE } from "../lib/demo";
import { phoneProblem } from "../lib/phone";
import { EMAIL_PATTERN } from "./steps";

const required = (label) => ({
  required: `Enter your ${label}`,
  validate: (v) => String(v).trim().length > 0 || `Enter your ${label}`,
});

function NameFields() {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <>
      <Field label="First name" error={errors.first_name?.message}>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className="cr-input"
            autoComplete="given-name"
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...register("first_name", required("first name"))}
          />
        )}
      </Field>
      <Field label="Last name" error={errors.last_name?.message}>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className="cr-input"
            autoComplete="family-name"
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...register("last_name", required("last name"))}
          />
        )}
      </Field>
    </>
  );
}

function CodeEntry({ verification, email }) {
  const titleId = useId();
  const hintId = useId();
  return (
    <div className="cr-otp">
      <p id={titleId} className="cr-label">
        Enter the 4-digit code we sent to {verification.codeSentTo}
      </p>
      <p id={hintId} className="cr-hint">
        {verification.demo
          ? `Sample role: no email is sent. The code is ${DEMO_CODE}.`
          : "It can take a minute to arrive. Check your spam folder too."}
      </p>
      <OtpInput
        key={verification.attempt}
        onComplete={verification.verify}
        busy={verification.busy === "verifying"}
        labelledBy={titleId}
        describedBy={hintId}
      />
      <div className="cr-otp-actions">
        {verification.busy === "verifying" && <span className="cr-hint">Checking…</span>}
        <button
          type="button"
          className="cr-link-btn"
          disabled={verification.cooldown > 0 || Boolean(verification.busy)}
          onClick={() => verification.send(email, { resend: true })}
        >
          {verification.cooldown > 0 ? `Send a new code in ${verification.cooldown}s` : "Send a new code"}
        </button>
        <button type="button" className="cr-link-btn" onClick={verification.cancelCode}>
          Use a different email
        </button>
      </div>
    </div>
  );
}

function EmailField({ verification }) {
  const {
    register,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const email = watch("email");
  const verified = verification.isVerified(email);
  const waitingForCode = Boolean(verification.codeSentTo) && !verified;

  const sendCode = async () => {
    if (await trigger("email")) verification.send(email);
  };

  const showVerifyNudge = errors.email_verified && !verified && !waitingForCode;
  const emailError = errors.email?.message || (showVerifyNudge ? errors.email_verified.message : "");

  return (
    <div className="cr-span-2 cr-field">
      <Field
        label="Email"
        hint={verified ? null : "We'll send a 4-digit code to check it's yours."}
        error={emailError}
      >
        {({ id, describedBy, invalid }) => (
          <div className="cr-input-row">
            <input
              id={id}
              className="cr-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              readOnly={verified || waitingForCode}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              {...register("email", {
                required: "Enter your email address",
                pattern: { value: EMAIL_PATTERN, message: "Enter an email address like name@example.com" },
              })}
            />
            {!verified && !waitingForCode && (
              <button
                type="button"
                className="cr-btn cr-btn--secondary"
                onClick={sendCode}
                disabled={verification.busy === "sending"}
              >
                {verification.busy === "sending" && <span className="cr-spinner" aria-hidden="true" />}
                Send code
              </button>
            )}
          </div>
        )}
      </Field>
      {verified && (
        <div className="cr-verified-row">
          <p className="cr-verified" role="status">
            <LuCircleCheck aria-hidden="true" /> Email confirmed
          </p>
          <button type="button" className="cr-link-btn" onClick={verification.reset}>
            Change email
          </button>
        </div>
      )}
      {waitingForCode && <CodeEntry verification={verification} email={email} />}
      <div role="alert">
        <FieldError message={verification.error} />
      </div>
    </div>
  );
}

function PhoneField() {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Field
      label="Mobile number"
      hint="We'll use it to arrange an interview. No code is sent to it."
      error={errors.phone?.message}
      className="cr-span-2"
    >
      {({ id, describedBy, invalid }) => (
        <Controller
          name="phone"
          control={control}
          rules={{ validate: (v) => phoneProblem(v?.number, v?.country) ?? true }}
          render={({ field }) => (
            <PhoneInput
              id={id}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={invalid}
              describedBy={describedBy}
            />
          )}
        />
      )}
    </Field>
  );
}

export default function ContactStep({ verification }) {
  return (
    <div className="cr-fields">
      <NameFields />
      <EmailField verification={verification} />
      <PhoneField />
    </div>
  );
}
